import { Button, Buttons, CancelButton, Drawer, Field, Form, FormMessage, Radio } from '~/components';
import { useApi, useSession, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useForm } from '~/hooks';
import React, { useRef } from 'react';
import { mergeValues } from '~/utils';
import * as Yup from 'yup';

export default function SyncSettingsDrawer({ integration, onClose, onSave }) {
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting }, form] = useForm();
  const api = useApi();
  const { workspace } = useWorkspace();
  const { isAdmin } = useSession();

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  const initialValues = mergeValues(
    { excludeInvoicedTime: true, importAsRoles: true, importTimeAsApproved: true },
    integration.settings,
  );

  return (
    <Drawer
      isOpen
      title="Settings"
      byline="Harvest Integration"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();

            await api.www.workspaces(workspace.id).harvest.updateSyncSettings({
              excludeInvoicedTime: values.excludeInvoicedTime,
              importTimeAsApproved: values.importTimeAsApproved,
              ...(isAdmin && { importAsRoles: values.importAsRoles }),
            });

            form.save();
            onSave();
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              excludeInvoicedTime: Yup.bool().label('Exclude Invoiced Time from Billing'),
              importTimeAsApproved: Yup.bool().label('Import All Time as Approved'),
              importAsRoles: Yup.bool().label('Import Harvest Tasks As'),
            })}>
            {(formik) => (
              <Form>
                {status && <FormMessage.Error>{message}</FormMessage.Error>}
                <Form.Section
                  title="Billing"
                  subtitle="Time entries and expenses that Harvest already invoiced can be kept out of Ready to Bill in Ruddr.">
                  <Form.Control>
                    <Field.Checkbox
                      name="excludeInvoicedTime"
                      label="Exclude time entries and expenses from billing when they already have an invoice in Harvest"
                    />
                  </Form.Control>
                </Form.Section>
                <Form.Section
                  title="Time Approvals"
                  subtitle="If turned on, time entries are marked approved when they are first migrated from Harvest. Otherwise, only time that is invoiced, approved, or locked in Harvest is marked approved.">
                  <Form.Control>
                    <Field.Checkbox name="importTimeAsApproved" label="Import All Time as Approved" />
                  </Form.Control>
                </Form.Section>
                {isAdmin && (
                  <Form.Section
                    title="Harvest Tasks"
                    subtitle="Harvest tracks time against tasks. Choose what a Harvest task becomes in Ruddr.">
                    <Form.Control>
                      <Field.RadioGroup name="importAsRoles" direction="vertical">
                        <Radio
                          value={true}
                          label={
                            <>
                              Import Harvest tasks as <strong>Ruddr roles</strong>. Choose this option if your Harvest
                              tasks are generic categories, such as "Project Management" or "Development".
                            </>
                          }
                        />
                        <Radio
                          value={false}
                          label={
                            <>
                              Import Harvest tasks as <strong>Ruddr tasks</strong>. Choose this option if your Harvest
                              tasks are specific pieces of work within a project.
                            </>
                          }
                        />
                      </Field.RadioGroup>
                    </Form.Control>
                  </Form.Section>
                )}
                <Drawer.Actions>
                  <Buttons align="right">
                    <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                    <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                      Save &amp; Close
                    </Button>
                  </Buttons>
                </Drawer.Actions>
              </Form>
            )}
          </Formik>
        );
      }}
    </Drawer>
  );
}
